import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { EntityDialog } from '../components/EntityDialog'
import { ProjectCard } from '../components/ProjectCard'
import {
  useGroupsWithProjects, useCreateGroup, useUpdateGroup, useDeleteGroup, useReorderGroups,
  useCreateProject,
} from '../lib/queries'
import { apiPut } from '../lib/api'
import { ICON_ARROW_DOWN, ICON_ARROW_UP, ICON_PENCIL, ICON_TRASH } from '../lib/icons'
import type { GroupDto, ProjectDto } from '../lib/types'

const DRAG_MIME = 'application/x-project-uuid'
const DRAG_GROUP_MIME = 'application/x-project-source-group'

interface DragInfo { uuid: string; sourceGroupUuid: string }

export function Groups() {
  const groups = useGroupsWithProjects()
  const createGroup = useCreateGroup()
  const updateGroup = useUpdateGroup()
  const deleteGroup = useDeleteGroup()
  const reorderGroups = useReorderGroups()

  const [groupModal, setGroupModal] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; group: GroupDto }
    | null
  >(null)
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState<GroupDto | null>(null)
  const [newProjectFor, setNewProjectFor] = useState<GroupDto | null>(null)

  const dragRef = useRef<DragInfo | null>(null)

  const list = (groups.data ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder)

  const moveGroup = (uuid: string, dir: -1 | 1) => {
    const idx = list.findIndex((g) => g.uuid === uuid)
    if (idx < 0) return
    const next = idx + dir
    if (next < 0 || next >= list.length) return
    const newOrder = list.slice()
    const [moved] = newOrder.splice(idx, 1)
    newOrder.splice(next, 0, moved)
    reorderGroups.mutate(newOrder.map((g) => g.uuid))
  }

  // 첫 진입 시 cache 가 없으면 본문 자체를 그리지 않음 — '+ New Group' 만 잠깐 보이는 깜빡임 방지.
  // (글로벌 progress bar 가 indicator). data 있으면 (cache hit 또는 fetch 완료) 한 번에 모두 렌더.
  const ready = !!groups.data

  return (
    <>
      {ready && (
        <div className="container" style={{ paddingTop: 24 }}>
          <div className="group-grid" id="group-list">
            {list.length === 0 && (
              <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 48 }}>
                No groups yet. Click "+ New Group" below to get started.
              </p>
            )}
            {list.map((g, i) => (
              <GroupSection
                key={g.uuid}
                group={g}
                projects={g.projects ?? []}
                index={i}
                total={list.length}
                dragRef={dragRef}
                onMoveUp={() => moveGroup(g.uuid, -1)}
                onMoveDown={() => moveGroup(g.uuid, 1)}
                onRename={() => setGroupModal({ mode: 'edit', group: g })}
                onDelete={() => setConfirmDeleteGroup(g)}
                onAddProject={() => setNewProjectFor(g)}
              />
            ))}
          </div>
          <div style={{ marginTop: 32 }}>
            <button
              id="btn-new-group"
              className="btn btn-ghost"
              style={{ border: '1px dashed var(--border-strong)', padding: '10px 18px' }}
              onClick={() => setGroupModal({ mode: 'create' })}
            >
              + New Group
            </button>
          </div>
        </div>
      )}

      <EntityDialog
        open={groupModal?.mode === 'create'}
        title="Create Group"
        id="modal-new-group"
        nameInputId="group-name"
        confirmButtonId="btn-create-group"
        nameLabel="Name"
        namePlaceholder="e.g. My Organization"
        confirmText="Create"
        onSubmit={({ name, description }) => {
          createGroup.mutate({ name, description }, { onSuccess: () => setGroupModal(null) })
        }}
        onCancel={() => setGroupModal(null)}
      />
      <EntityDialog
        open={groupModal?.mode === 'edit'}
        title="Rename Group"
        id="modal-rename-group"
        nameInputId="group-name"
        confirmButtonId="btn-save-group"
        nameLabel="Name"
        defaultName={groupModal?.mode === 'edit' ? groupModal.group.name : ''}
        defaultDesc={groupModal?.mode === 'edit' ? (groupModal.group.description ?? '') : ''}
        confirmText="Save"
        onSubmit={({ name, description }) => {
          if (groupModal?.mode !== 'edit') return
          updateGroup.mutate(
            { uuid: groupModal.group.uuid, req: { name, description } },
            { onSuccess: () => setGroupModal(null) },
          )
        }}
        onCancel={() => setGroupModal(null)}
      />
      <ConfirmDialog
        open={!!confirmDeleteGroup}
        title={`Delete group '${confirmDeleteGroup?.name ?? ''}'?`}
        message="The group and its projects will be archived (recoverable from Trash)."
        confirmText="Delete"
        danger
        onConfirm={() => {
          if (confirmDeleteGroup) deleteGroup.mutate(confirmDeleteGroup.uuid, { onSuccess: () => setConfirmDeleteGroup(null) })
        }}
        onCancel={() => setConfirmDeleteGroup(null)}
      />
      <NewProjectDialog
        groupForCreate={newProjectFor}
        onClose={() => setNewProjectFor(null)}
      />
    </>
  )
}

interface GroupSectionProps {
  group: GroupDto
  projects: ProjectDto[]
  index: number
  total: number
  dragRef: React.MutableRefObject<DragInfo | null>
  onMoveUp: () => void
  onMoveDown: () => void
  onRename: () => void
  onDelete: () => void
  onAddProject: () => void
}

const GROUPS_KEY = ['groups', { include: 'projects' }] as const

/** drop 시 source 카드 element 의 clone 을 viewport 의 같은 위치에 띄우고 0.2s 동안 opacity/scale fade-out.
 *  React 가 setQueryData 후 source 카드를 즉시 unmount 해도 시각적으로 부드러운 사라짐 제공. */
function fadeOutClone(el: HTMLElement) {
  const r = el.getBoundingClientRect()
  const clone = el.cloneNode(true) as HTMLElement
  clone.removeAttribute('id')
  clone.classList.remove('dragging', 'drop-before', 'drop-after')
  Object.assign(clone.style, {
    position: 'fixed',
    left: r.left + 'px', top: r.top + 'px',
    width: r.width + 'px', height: r.height + 'px',
    margin: '0', pointerEvents: 'none',
    transition: 'opacity .2s ease, transform .2s ease',
    zIndex: '50',
  } as Partial<CSSStyleDeclaration>)
  document.body.appendChild(clone)
  requestAnimationFrame(() => {
    clone.style.opacity = '0'
    clone.style.transform = 'scale(0.92)'
  })
  setTimeout(() => clone.remove(), 220)
}

function GroupSection({ group, projects, index, total, dragRef, onMoveUp, onMoveDown, onRename, onDelete, onAddProject }: GroupSectionProps) {
  const qc = useQueryClient()

  const sectionRef = useRef<HTMLElement>(null)

  const onDragStart = (e: React.DragEvent, project: ProjectDto, sourceGroupUuid: string) => {
    if (!e.dataTransfer) return
    e.dataTransfer.setData(DRAG_MIME, project.uuid)
    e.dataTransfer.setData(DRAG_GROUP_MIME, sourceGroupUuid)
    e.dataTransfer.effectAllowed = 'move'
    const card = (e.target as HTMLElement).closest('.project-card') as HTMLElement | null
    if (card) {
      const r = card.getBoundingClientRect()
      e.dataTransfer.setDragImage(card, e.clientX - r.left, e.clientY - r.top)
      card.classList.add('dragging')
    }
    dragRef.current = { uuid: project.uuid, sourceGroupUuid }
  }
  const onDragEnd = (e: React.DragEvent) => {
    const card = (e.target as HTMLElement).closest('.project-card') as HTMLElement | null
    card?.classList.remove('dragging')
    document.querySelectorAll('.project-card.drop-before, .project-card.drop-after').forEach((el) => {
      el.classList.remove('drop-before', 'drop-after')
    })
    document.querySelectorAll('.group-section.drop-target').forEach((el) => {
      el.classList.remove('drop-target')
    })
    dragRef.current = null
  }

  const onSectionDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return
    const dragged = dragRef.current
    if (!dragged || dragged.sourceGroupUuid === group.uuid) return
    e.preventDefault()
    sectionRef.current?.classList.add('drop-target')
  }
  const onSectionDragLeave = (e: React.DragEvent) => {
    const related = e.relatedTarget as Node | null
    if (related && sectionRef.current?.contains(related)) return
    sectionRef.current?.classList.remove('drop-target')
  }

  const onCardDragOver = (e: React.DragEvent, project: ProjectDto, _g: string) => {
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return
    const dragged = dragRef.current
    if (dragged?.uuid === project.uuid) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const card = (e.target as HTMLElement).closest('.project-card') as HTMLElement | null
    if (!card) return
    const rect = card.getBoundingClientRect()
    const before = (e.clientY - rect.top) < rect.height / 2
    card.classList.toggle('drop-before', before)
    card.classList.toggle('drop-after', !before)
  }
  const onCardDragLeave = (e: React.DragEvent) => {
    const card = (e.target as HTMLElement).closest('.project-card') as HTMLElement | null
    if (!card) return
    if (!card.contains(e.relatedTarget as Node)) {
      card.classList.remove('drop-before', 'drop-after')
    }
  }
  const onCardDrop = async (e: React.DragEvent, refProject: ProjectDto, targetGroupUuid: string) => {
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return
    e.preventDefault()
    e.stopPropagation()
    const draggedUuid = e.dataTransfer.getData(DRAG_MIME)
    const sourceGroupUuid = e.dataTransfer.getData(DRAG_GROUP_MIME)
    const card = (e.target as HTMLElement).closest('.project-card') as HTMLElement | null
    const before = card?.classList.contains('drop-before') ?? true
    card?.classList.remove('drop-before', 'drop-after')
    if (!draggedUuid || draggedUuid === refProject.uuid) return
    await applyDrop(draggedUuid, sourceGroupUuid, targetGroupUuid, refProject.uuid, before)
  }
  const onGridDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }
  const onGridDrop = async (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return
    e.preventDefault()
    const draggedUuid = e.dataTransfer.getData(DRAG_MIME)
    const sourceGroupUuid = e.dataTransfer.getData(DRAG_GROUP_MIME)
    if (!draggedUuid) return
    await applyDrop(draggedUuid, sourceGroupUuid, group.uuid, null, false)
  }
  // + New Project 카드: 그룹 끝 (또는 빈 그룹의 첫 자리) 에 drop indicator
  const onAddCardDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    ;(e.currentTarget as HTMLElement).classList.add('drop-before')
  }
  const onAddCardDragLeave = (e: React.DragEvent) => {
    const el = e.currentTarget as HTMLElement
    if (!el.contains(e.relatedTarget as Node)) el.classList.remove('drop-before')
  }
  const onAddCardDrop = async (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return
    e.preventDefault(); e.stopPropagation()
    const draggedUuid = e.dataTransfer.getData(DRAG_MIME)
    const sourceGroupUuid = e.dataTransfer.getData(DRAG_GROUP_MIME)
    ;(e.currentTarget as HTMLElement).classList.remove('drop-before')
    if (!draggedUuid) return
    await applyDrop(draggedUuid, sourceGroupUuid, group.uuid, null, false)
  }

  const applyDrop = async (draggedUuid: string, sourceGroupUuid: string, targetGroupUuid: string, refUuid: string | null, before: boolean) => {
    sectionRef.current?.classList.remove('drop-target')
    const snapshot = qc.getQueryData<GroupDto[]>(GROUPS_KEY)
    if (!snapshot) return
    const dragged = snapshot.flatMap((g) => g.projects ?? []).find((p) => p.uuid === draggedUuid)
    if (!dragged) return

    // cross-group 시 source 카드를 잠시 fade-out clone — cache update 가 즉시라 unmount 가 곧장
    // 일어나면서 시각적으로 카드가 '뚝' 사라지는 인상을 부드럽게.
    if (sourceGroupUuid !== targetGroupUuid) {
      const sourceEl = document.querySelector<HTMLElement>(
        `.project-card[data-project-uuid="${draggedUuid}"][data-source-group-uuid="${sourceGroupUuid}"]`)
      if (sourceEl) fadeOutClone(sourceEl)
    }

    // 새 cache 모양 — source 에서 제거 + target 의 desired position 에 insert (cross-group, same-group 둘 다)
    const next: GroupDto[] = snapshot.map((g) => {
      if (g.uuid !== sourceGroupUuid && g.uuid !== targetGroupUuid) return g
      let list = (g.projects ?? []).filter((p) => p.uuid !== draggedUuid)
      if (g.uuid === targetGroupUuid) {
        let insertAt = list.length
        if (refUuid) {
          const refIdx = list.findIndex((p) => p.uuid === refUuid)
          if (refIdx >= 0) insertAt = before ? refIdx : refIdx + 1
        }
        list = [...list.slice(0, insertAt), dragged, ...list.slice(insertAt)]
      }
      return { ...g, projects: list }
    })

    // optimistic — UI 즉시 반영
    qc.setQueryData(GROUPS_KEY, next)

    // backend sync — invalidate 거치지 않고 직접 apiPut.
    // (mutation hook 사용 시 onSuccess invalidate → refetch 가 우리 optimistic cache 를 잠시
    //  덮어 깜빡임. 직접 호출 + 우리 cache 유지.)
    const targetGroup = next.find((g) => g.uuid === targetGroupUuid)
    const targetUuids = (targetGroup?.projects ?? []).map((p) => p.uuid)
    try {
      if (sourceGroupUuid !== targetGroupUuid) {
        await apiPut(`/api/projects/${draggedUuid}/move`, { targetGroupUuid })
      }
      await apiPut(`/api/groups/${targetGroupUuid}/projects/reorder`, { uuids: targetUuids })
    } catch (err) {
      qc.setQueryData(GROUPS_KEY, snapshot)
      console.error('[project drop] failed, rolled back', err)
    }
  }

  return (
    <section
      ref={sectionRef}
      className="group-section actions-host"
      data-group-uuid={group.uuid}
      onDragEnter={onSectionDragEnter}
      onDragLeave={onSectionDragLeave}
    >
      <div className="group-section-header">
        <div className="group-section-titles">
          <h2 className="group-section-name">{group.name}</h2>
          {group.description && <span className="group-section-meta">{group.description}</span>}
        </div>
        <div className="group-section-actions">
          <button type="button" className="group-icon-btn group-up-btn"
                  onClick={onMoveUp} disabled={index === 0}
                  title="Move up" aria-label="Move group up"
                  dangerouslySetInnerHTML={{ __html: ICON_ARROW_UP }} />
          <button type="button" className="group-icon-btn group-down-btn"
                  onClick={onMoveDown} disabled={index === total - 1}
                  title="Move down" aria-label="Move group down"
                  dangerouslySetInnerHTML={{ __html: ICON_ARROW_DOWN }} />
          <button type="button" className="group-icon-btn group-rename-btn"
                  onClick={onRename}
                  title="Rename" aria-label="Rename group"
                  dangerouslySetInnerHTML={{ __html: ICON_PENCIL }} />
          <button type="button" className="group-icon-btn group-delete-btn"
                  onClick={onDelete}
                  title="Delete" aria-label="Delete group"
                  dangerouslySetInnerHTML={{ __html: ICON_TRASH }} />
        </div>
      </div>
      <div className="project-grid" onDragOver={onGridDragOver} onDrop={onGridDrop}>
        {projects.map((p) => (
          <ProjectCard
            key={p.uuid}
            project={p}
            groupUuid={group.uuid}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragOver={onCardDragOver}
            onDragLeave={onCardDragLeave}
            onDrop={onCardDrop}
          />
        ))}
        <button
          type="button"
          className="card project-card project-card-add"
          data-group-uuid={group.uuid}
          onClick={onAddProject}
          onDragOver={onAddCardDragOver}
          onDragLeave={onAddCardDragLeave}
          onDrop={onAddCardDrop}
        >
          <span className="project-card-add-plus">+</span>
          <span className="project-card-add-label">New Project</span>
        </button>
      </div>
    </section>
  )
}

function NewProjectDialog({ groupForCreate, onClose }: { groupForCreate: GroupDto | null; onClose: () => void }) {
  const createProject = useCreateProject()
  return (
    <EntityDialog
      open={!!groupForCreate}
      title="Create Project"
      id="modal-new-project"
      nameInputId="project-name"
      confirmButtonId="btn-create-project"
      nameLabel="Project Name"
      namePlaceholder="e.g. Documentation"
      confirmText="Create"
      onSubmit={({ name, description }) => {
        if (!groupForCreate) return
        createProject.mutate(
          { groupUuid: groupForCreate.uuid, req: { name, description, publicReadable: false } },
          { onSuccess: () => onClose() },
        )
      }}
      onCancel={onClose}
    />
  )
}
